import { ReactNode, useId } from "react"

import { Box, Chip, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { DashboardTab } from "ts/types/AdminDashboard"

import CustomRangeDialog from "./CustomRangeDialog"
import { filterChipSx } from "./FilterBar.styles"
import useDashboardPeriod, { DASHBOARD_PRESETS } from "./useDashboardPeriod"

const FilterBar = ({
  tab,
  children,
}: {
  tab: DashboardTab
  children?: ReactNode
}) => {
  const { t } = useTranslation()
  const { params, setPreset, setCustomRange } = useDashboardPeriod(tab)
  const timeSpanLabelId = useId()

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { mobile: "1fr", tablet: "max-content 1fr" },
        alignItems: "center",
        columnGap: 3,
        rowGap: 1.5,
      }}
    >
      <Typography
        variant="body1"
        color="mockup.neutral30"
        fontWeight={700}
        id={timeSpanLabelId}
      >
        {t("adminDashboard.timeSpan")}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        sx={{ bgcolor: "mockup.primary95", p: 0.5, borderRadius: "20px" }}
      >
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          role="group"
          aria-labelledby={timeSpanLabelId}
        >
          {DASHBOARD_PRESETS.map((preset) => (
            <Chip
              key={preset}
              label={t(`adminDashboard.preset_${preset}`)}
              color={params.preset === preset ? "primary" : "default"}
              variant={params.preset === preset ? "filled" : "outlined"}
              onClick={() => setPreset(preset)}
              aria-pressed={params.preset === preset}
              sx={filterChipSx(params.preset === preset)}
            />
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} ml="auto">
          <Typography variant="body1" color="mockup.neutral10" fontWeight={500}>
            {t("adminDashboard.custom")}
          </Typography>
          <CustomRangeDialog
            key={`${params.date_from}-${params.date_to}`}
            params={params}
            onApply={setCustomRange}
          />
        </Stack>
      </Stack>
      {children && (
        <Stack sx={{ gridColumn: { tablet: 2 } }}>{children}</Stack>
      )}
    </Box>
  )
}

export default FilterBar
